import { useState, useEffect, useRef, useCallback } from 'react'
import { FiEdit2, FiTrash2, FiDownload, FiUpload, FiPlay, FiPause, FiX } from 'react-icons/fi'
import { themes, type ThemeSummary } from '../api/client'
import { useTranslation } from '../i18n/LanguageContext'
import { styles, tierBadgeStyle } from './adminStyles'

/* ── Theme List View ─────────────────────────────────────────── */

interface ThemeListViewProps {
  onSelect: (slug: string) => void
  onBack:   () => void
}

export function ThemeListView({ onSelect, onBack }: ThemeListViewProps) {
  const { t } = useTranslation()
  const [themeList, setThemeList]     = useState<ThemeSummary[]>([])
  const [loading, setLoading]         = useState(true)
  const [showCreate, setShowCreate]   = useState(false)
  const [createForm, setCreateForm]   = useState({ slug: '', nameEs: '', nameEn: '', description: '', requiredTier: 'BASIC' as 'BASIC' | 'PLUS' | 'PREMIUM' })
  const [creating, setCreating]       = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // AI generate modal state
  const [showGenerate, setShowGenerate]   = useState(false)
  const [generatePrompt, setGeneratePrompt] = useState('')
  const [generating, setGenerating]       = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)

  // Import state
  const importInputRef = useRef<HTMLInputElement>(null)
  const [importing, setImporting]     = useState(false)
  const [importError, setImportError] = useState<string | null>(null)

  // Error detail expansion + retry
  const [errorSlug, setErrorSlug]   = useState<string | null>(null)
  const [retryingSlug, setRetryingSlug] = useState<string | null>(null)
  const [retryPrompt, setRetryPrompt] = useState<string>('')

  // Edit state
  const [editingSlug, setEditingSlug] = useState<string | null>(null)
  const [editForm, setEditForm]       = useState({ nameEs: '', nameEn: '', description: '', requiredTier: 'BASIC' as 'BASIC' | 'PLUS' | 'PREMIUM' })
  const [editSaving, setEditSaving]   = useState(false)
  const [editError, setEditError]     = useState<string | null>(null)

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const loadThemes = useCallback(() => {
    return themes.listAll()
      .then(setThemeList)
      .catch(() => {})
  }, [])

  useEffect(() => {
    loadThemes().finally(() => setLoading(false))
  }, [loadThemes])

  // Poll every 3 s while any theme is IN_PROGRESS
  useEffect(() => {
    const hasInProgress = themeList.some((t) => t.generationStatus === 'IN_PROGRESS')
    if (hasInProgress && !pollRef.current) {
      pollRef.current = setInterval(loadThemes, 3000)
    } else if (!hasInProgress && pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    }
  }, [themeList, loadThemes])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!createForm.slug || !createForm.nameEs || !createForm.nameEn) {
      setCreateError('Slug, nombre ES y nombre EN son obligatorios')
      return
    }
    setCreating(true)
    setCreateError(null)
    try {
      await themes.create({
        slug:         createForm.slug,
        nameEs:       createForm.nameEs,
        nameEn:       createForm.nameEn,
        description:  createForm.description || undefined,
        requiredTier: createForm.requiredTier,
      })
      await loadThemes()
      setCreateForm({ slug: '', nameEs: '', nameEn: '', description: '', requiredTier: 'BASIC' })
      setShowCreate(false)
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : 'Error al crear el tema')
    } finally {
      setCreating(false)
    }
  }

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!generatePrompt.trim()) {
      setGenerateError('Escribe un prompt para generar el tema')
      return
    }
    setGenerating(true)
    setGenerateError(null)
    try {
      await themes.generate(generatePrompt.trim())
      await loadThemes()
      setGeneratePrompt('')
      setShowGenerate(false)
    } catch (err: unknown) {
      setGenerateError(err instanceof Error ? err.message : 'Error al iniciar la generación')
    } finally {
      setGenerating(false)
    }
  }

  const handleRetry = async (slug: string, prompt?: string) => {
    setRetryingSlug(slug)
    try {
      await themes.retry(slug, prompt && prompt !== themeList.find(t => t.slug === slug)?.generationPrompt ? prompt : undefined)
      setErrorSlug(null)
      setRetryPrompt('')
      await loadThemes()
    } catch { /* error stays visible in the box */ }
    finally { setRetryingSlug(null) }
  }

  const startEdit = (theme: ThemeSummary) => {
    setEditingSlug(theme.slug)
    setEditForm({ nameEs: theme.nameEs, nameEn: theme.nameEn, description: theme.description ?? '', requiredTier: theme.requiredTier })
    setEditError(null)
  }

  const cancelEdit = () => { setEditingSlug(null); setEditError(null) }

  const saveEdit = async (slug: string) => {
    if (!editForm.nameEs || !editForm.nameEn) {
      setEditError('Nombre ES y EN son obligatorios')
      return
    }
    setEditSaving(true)
    setEditError(null)
    try {
      await themes.update(slug, {
        nameEs:       editForm.nameEs,
        nameEn:       editForm.nameEn,
        description:  editForm.description || undefined,
        requiredTier: editForm.requiredTier,
      })
      await loadThemes()
      setEditingSlug(null)
    } catch (err: unknown) {
      setEditError(err instanceof Error ? err.message : 'Error al guardar')
    } finally {
      setEditSaving(false)
    }
  }

  const togglePublished = async (theme: ThemeSummary) => {
    const action = theme.published ? 'despublicar' : 'publicar'
    if (!confirm(`¿Quieres ${action} el tema "${theme.nameEs}"?`)) return
    try {
      await themes.update(theme.slug, { published: !theme.published })
      setThemeList((prev) => prev.map((t) =>
        t.slug === theme.slug ? { ...t, published: !t.published } : t
      ))
    } catch { /* intentionally empty */ }
  }

  const handleDelete = async (theme: ThemeSummary) => {
    if (!confirm(`¿Eliminar el tema "${theme.nameEs}" y todas sus imágenes? Esta acción no se puede deshacer.`)) return
    try {
      await themes.delete(theme.slug)
      setThemeList((prev) => prev.filter((t) => t.slug !== theme.slug))
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Error al eliminar el tema')
    }
  }

  const handleExport = async (e: React.MouseEvent, slug: string) => {
    e.stopPropagation()
    try {
      const blob = await themes.export(slug)
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = `${slug}.zip`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Error al exportar el tema')
    }
  }

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setImporting(true)
    setImportError(null)
    try {
      const result = await themes.import(file)
      await loadThemes()
      onSelect(result.slug)
    } catch (err: unknown) {
      setImportError(err instanceof Error ? err.message : 'Error al importar el tema')
    } finally {
      setImporting(false)
    }
  }

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <button onClick={onBack} style={styles.backBtn}>{t.adminHub.backToAdmin}</button>
        <h1 style={styles.headerTitle}>{t.adminHub.themesTitle}</h1>
      </header>

      <div style={styles.content}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Temas de imágenes</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={() => { setShowGenerate((v) => !v); setShowCreate(false) }}
              style={styles.aiBtn}
            >
              {showGenerate ? <><FiX size={13} style={{ marginRight: 4 }} />Cancelar</> : '✨ Generar con IA'}
            </button>
            <button
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
              style={styles.newBtn}
            >
              {importing ? 'Importando…' : <><FiUpload size={13} style={{ marginRight: 4 }} />Importar tema</>}
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept=".zip"
              style={{ display: 'none' }}
              onChange={handleImportFile}
            />
            <button
              onClick={() => { setShowCreate((v) => !v); setShowGenerate(false) }}
              style={styles.newBtn}
            >
              {showCreate ? <><FiX size={13} style={{ marginRight: 4 }} />Cancelar</> : '+ Nuevo tema'}
            </button>
          </div>
          {importError && <p style={{ ...styles.errorText, marginTop: 8 }}>{importError}</p>}
        </div>

        {/* AI Generate form */}
        {showGenerate && (
          <form onSubmit={handleGenerate} style={styles.createForm}>
            <div style={styles.formField}>
              <label style={styles.label}>Prompt de generación</label>
              <textarea
                style={{ ...styles.input, resize: 'vertical', minHeight: 72, fontFamily: 'Nunito, sans-serif' }}
                placeholder='Ej: "Un tema de ropa, con bufanda, falda, sombrero, corbata, chaqueta y camiseta"'
                value={generatePrompt}
                onChange={(e) => setGeneratePrompt(e.target.value)}
                rows={3}
              />
              <p style={{ fontSize: '0.875rem', color: '#9A8070', margin: '4px 0 0' }}>
                La IA generará nombres, descripciones e imágenes para cada objeto del tema.
                El resultado aparecerá en borrador para que lo revises antes de publicar.
              </p>
            </div>
            {generateError && <p style={styles.errorText}>{generateError}</p>}
            <button type="submit" disabled={generating} style={styles.submitBtn}>
              {generating ? 'Iniciando…' : '✨ Generar tema'}
            </button>
          </form>
        )}

        {/* Manual Create form */}
        {showCreate && (
          <form onSubmit={handleCreate} style={styles.createForm}>
            <div style={styles.formRow}>
              <div style={styles.formField}>
                <label style={styles.label}>Slug</label>
                <input
                  style={styles.input}
                  placeholder="primavera"
                  value={createForm.slug}
                  onChange={(e) => setCreateForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                />
              </div>
              <div style={styles.formField}>
                <label style={styles.label}>Nombre (ES)</label>
                <input
                  style={styles.input}
                  placeholder="Primavera"
                  value={createForm.nameEs}
                  onChange={(e) => setCreateForm((f) => ({ ...f, nameEs: e.target.value }))}
                />
              </div>
              <div style={styles.formField}>
                <label style={styles.label}>Nombre (EN)</label>
                <input
                  style={styles.input}
                  placeholder="Spring"
                  value={createForm.nameEn}
                  onChange={(e) => setCreateForm((f) => ({ ...f, nameEn: e.target.value }))}
                />
              </div>
            </div>
            <div style={{ ...styles.formRow }}>
              <div style={styles.formField}>
                <label style={styles.label}>Descripción (opcional)</label>
                <input
                  style={styles.input}
                  placeholder="Flores y naturaleza de primavera"
                  value={createForm.description}
                  onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div style={{ ...styles.formField, maxWidth: 140 }}>
                <label style={styles.label}>Tier</label>
                <select
                  style={styles.input}
                  value={createForm.requiredTier}
                  onChange={(e) => setCreateForm((f) => ({ ...f, requiredTier: e.target.value as 'BASIC' | 'PLUS' | 'PREMIUM' }))}
                >
                  <option value="BASIC">BASIC</option>
                  <option value="PLUS">PLUS</option>
                  <option value="PREMIUM">PREMIUM</option>
                </select>
              </div>
            </div>
            {createError && <p style={styles.errorText}>{createError}</p>}
            <button type="submit" disabled={creating} style={styles.submitBtn}>
              {creating ? 'Creando…' : 'Crear tema'}
            </button>
          </form>
        )}

        {/* Themes table */}
        {loading ? (
          <p style={styles.emptyText}>Cargando…</p>
        ) : themeList.length === 0 ? (
          <p style={styles.emptyText}>No hay temas. Crea el primero.</p>
        ) : (
          <div style={styles.themeTable}>
            {themeList.map((theme) => (
              <div
                key={theme.slug}
                style={{
                  ...styles.themeRow,
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 0,
                  cursor: editingSlug === theme.slug ? 'default' : 'pointer',
                }}
                onClick={() => { if (editingSlug !== theme.slug) onSelect(theme.slug) }}
              >
                {/* Main row content */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                {editingSlug === theme.slug ? (
                  /* ── Inline edit form ── */
                  <div style={{ flex: 1 }}>
                    <div style={styles.formRow}>
                      <div style={styles.formField}>
                        <label style={styles.label}>Nombre (ES)</label>
                        <input
                          style={styles.input}
                          value={editForm.nameEs}
                          onChange={(e) => setEditForm((f) => ({ ...f, nameEs: e.target.value }))}
                          autoFocus
                        />
                      </div>
                      <div style={styles.formField}>
                        <label style={styles.label}>Nombre (EN)</label>
                        <input
                          style={styles.input}
                          value={editForm.nameEn}
                          onChange={(e) => setEditForm((f) => ({ ...f, nameEn: e.target.value }))}
                        />
                      </div>
                      <div style={styles.formField}>
                        <label style={styles.label}>Descripción</label>
                        <input
                          style={styles.input}
                          placeholder="(opcional)"
                          value={editForm.description}
                          onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                        />
                      </div>
                      <div style={styles.formField}>
                        <label style={styles.label}>Tier</label>
                        <select
                          style={styles.input}
                          value={editForm.requiredTier}
                          onChange={(e) => setEditForm((f) => ({ ...f, requiredTier: e.target.value as 'BASIC' | 'PLUS' | 'PREMIUM' }))}
                        >
                          <option value="BASIC">BASIC</option>
                          <option value="PLUS">PLUS</option>
                          <option value="PREMIUM">PREMIUM</option>
                        </select>
                      </div>
                    </div>
                    {editError && <p style={{ ...styles.errorText, marginTop: 8 }}>{editError}</p>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button
                        onClick={() => saveEdit(theme.slug)}
                        disabled={editSaving}
                        style={styles.submitBtn}
                      >
                        {editSaving ? 'Guardando…' : 'Guardar'}
                      </button>
                      <button onClick={cancelEdit} style={styles.cancelBtn}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Normal row ── */
                  <>
                    <div style={styles.themeInfo}>
                      {theme.coverUrl
                        ? <img src={theme.coverUrl} alt="" style={styles.thumbSmall} />
                        : <div style={styles.thumbPlaceholder}>
                            {theme.generationStatus === 'IN_PROGRESS' ? '⏳' : '🖼️'}
                          </div>
                      }
                      <div style={{ minWidth: 0 }}>
                        <div style={styles.themeNames}>
                          <span style={styles.themeName}>{theme.nameEs}</span>
                          <span style={styles.themeNameEn}> / {theme.nameEn}</span>
                        </div>
                        {theme.description && (
                          <p style={styles.themeDesc}>{theme.description}</p>
                        )}
                        <div style={styles.themeMeta}>
                          <code style={styles.slugBadge}>{theme.slug}</code>
                          <span style={styles.imageCount}>{theme.imageCount} img</span>
                          <span style={{ ...styles.statusBadge, ...tierBadgeStyle(theme.requiredTier) }}>
                            {theme.requiredTier}
                          </span>
                          {/* Published / generation status badges inline with meta */}
                          {theme.generationStatus !== 'IN_PROGRESS' && (
                            <span style={{ ...styles.statusBadge, ...(theme.published ? styles.statusPublished : styles.statusDraft) }}>
                              {theme.published ? 'Publicado' : 'Borrador'}
                            </span>
                          )}
                          {theme.generationStatus !== 'MANUAL' && (
                            theme.generationStatus === 'FAILED' ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); setErrorSlug((s) => s === theme.slug ? null : theme.slug) }}
                                style={{ ...styles.statusBadge, ...styles.statusFailed, border: 'none', cursor: 'pointer' }}
                              >
                                ✗ Error
                              </button>
                            ) : (
                              <span style={{
                                ...styles.statusBadge,
                                ...(theme.generationStatus === 'IN_PROGRESS' ? styles.statusGenerating :
                                    theme.generationStatus === 'DRAFT'       ? styles.statusDraft :
                                    styles.statusPublished),
                              }}>
                                {theme.generationStatus === 'IN_PROGRESS' ? '⏳ Generando' :
                                 theme.generationStatus === 'DRAFT'       ? '✨ IA' : 'IA'}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={styles.themeActions}>
                      <button
                        onClick={(e) => { e.stopPropagation(); togglePublished(theme) }}
                        style={styles.toggleBtn}
                        disabled={theme.generationStatus === 'IN_PROGRESS'}
                        title={theme.published ? 'Despublicar' : 'Publicar'}
                      >
                        {theme.published ? <FiPause size={14} /> : <FiPlay size={14} />}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); startEdit(theme) }}
                        style={styles.toggleBtn}
                        disabled={theme.generationStatus === 'IN_PROGRESS'}
                        title="Editar nombres"
                      >
                        <FiEdit2 size={14} />
                      </button>
                      <button
                        onClick={(e) => handleExport(e, theme.slug)}
                        style={styles.toggleBtn}
                        title="Exportar tema como ZIP"
                      >
                        <FiDownload size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(theme) }}
                        style={styles.deleteIconBtn}
                        title="Eliminar tema"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </>
                )}
                </div>{/* end main row */}

                {/* Error detail box — shown when the error badge is clicked */}
                {theme.generationStatus === 'FAILED' && errorSlug === theme.slug && (
                  <div style={styles.errorBox} onClick={(e) => e.stopPropagation()}>
                    <div style={styles.errorBoxHeader}>
                      <span style={styles.errorBoxTitle}>Error de generación</span>
                      <button onClick={() => { setErrorSlug(null); setRetryPrompt('') }} style={styles.errorBoxClose}><FiX size={14} /></button>
                    </div>
                    {theme.generationPrompt && (
                      <div style={{ marginTop: 8 }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>
                          Prompt (editable)
                        </label>
                        <textarea
                          style={{ ...styles.input, resize: 'vertical', minHeight: 60, fontFamily: 'Nunito, sans-serif' }}
                          value={retryPrompt || theme.generationPrompt}
                          onChange={(e) => setRetryPrompt(e.target.value)}
                          rows={3}
                        />
                      </div>
                    )}
                    {theme.generationError && (
                      <pre style={styles.errorBoxMessage}>{theme.generationError}</pre>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button
                        onClick={() => {
                          const currentTheme = themeList.find(t => t.slug === theme.slug)
                          const promptToUse = retryPrompt || (currentTheme?.generationPrompt ?? '')
                          handleRetry(theme.slug, promptToUse || undefined)
                        }}
                        disabled={retryingSlug === theme.slug}
                        style={styles.retryBtn}
                      >
                        {retryingSlug === theme.slug ? 'Reintentando…' : '↺ Reintentar'}
                      </button>
                      <button onClick={() => { setErrorSlug(null); setRetryPrompt('') }} style={styles.cancelBtn}>
                        Cerrar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
